import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
/**
 * Interface para dados de adesão ao tratamento (v2)
 */
export interface TreatmentAdherence {
  id: string;
  member_id: string;
  total_doses_expected: number;
  doses_taken_on_time: number;
  doses_taken_late: number;
  doses_missed: number;
  partial_doses_number: number; // ✅ NOVA COLUNA v2
  adherence_percentage: number;
  created_at: string;
  updated_at: string;
}

/**
 * Serviço para gerenciar adesão aos tratamentos
 */
class AdherenceService {
  private static instance: AdherenceService;

  private constructor() {}

  static getInstance(): AdherenceService {
    if (!AdherenceService.instance) {
      AdherenceService.instance = new AdherenceService();
    }
    return AdherenceService.instance;
  }

  /**
   * Busca a adesão de um membro específico (v2)
   * @param memberId - ID do membro
   * @returns Dados de adesão ou null se não encontrado
   */
  async getMemberAdherence(memberId: string): Promise<TreatmentAdherence | null> {
    try {
      logger.sensitive('Buscando adesão do membro (v2)', { memberId });

      const { data, error } = await supabase
        .from('treatment_adherence_v2')
        .select('*')
        .eq('member_id', memberId)
        .single();

      if (error) {
        // Se não existe registro, retorna valores zerados
        if (error.code === 'PGRST116') {
          console.log('ℹ️ [AdherenceService] Nenhum registro de adesão encontrado, retornando valores padrão');
          return {
            id: '',
            member_id: memberId,
            total_doses_expected: 0,
            doses_taken_on_time: 0,
            doses_taken_late: 0,
            doses_missed: 0,
            partial_doses_number: 0,
            adherence_percentage: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        }
        throw error;
      }

      logger.debug('Adesão encontrada (v2)', { percentage: data?.adherence_percentage, expectativa: data?.total_doses_expected });
      return data as unknown as TreatmentAdherence;
    } catch (error: any) {
      console.error('❌ [AdherenceService] Erro ao buscar adesão:', error);
      throw new Error(`Erro ao buscar adesão: ${error.message}`);
    }
  }

  /**
   * Atualiza manualmente a adesão de um membro (v2)
   * Chama a função do banco que recalcula todos os valores
   * @param memberId - ID do membro
   */
  async updateMemberAdherence(memberId: string): Promise<void> {
    try {
      console.log('🔄 [AdherenceService] Atualizando adesão do membro (v2):', memberId);

      const { error } = await supabase.rpc('recalculate_treatment_adherence_v2', {
        p_member_id: memberId
      });

      if (error) throw error;

      console.log('✅ [AdherenceService] Adesão atualizada com sucesso (v2)');
    } catch (error: any) {
      console.error('❌ [AdherenceService] Erro ao atualizar adesão:', error);
      throw new Error(`Erro ao atualizar adesão: ${error.message}`);
    }
  }

  /**
   * Calcula o total de doses esperadas para uma medicação específica
   * @param medicationId - ID da medicação
   * @returns Total de doses esperadas
   */
  async calculateTotalDosesForMedication(medicationId: string): Promise<number> {
    try {
      console.log('🧮 [AdherenceService] Calculando doses totais para medicação:', medicationId);

      const { data, error } = await supabase.rpc('calculate_total_doses_for_medication' as any, {
        p_medication_id: medicationId
      });

      if (error) throw error;

      console.log('✅ [AdherenceService] Total de doses calculado:', data);
      return data as number;
    } catch (error: any) {
      console.error('❌ [AdherenceService] Erro ao calcular doses:', error);
      throw new Error(`Erro ao calcular doses: ${error.message}`);
    }
  }

  /**
   * Busca adesão de todos os membros de uma família (v2)
   * @param familyId - ID da família
   * @returns Array com adesão de cada membro
   */
  async getFamilyAdherence(familyId: string): Promise<TreatmentAdherence[]> {
    try {
      logger.sensitive('Buscando adesão da família (v2)', { familyId });

      // Primeiro buscar todos os membros da família
      const { data: members, error: membersError } = await supabase
        .from('family_members')
        .select('id')
        .eq('family_id', familyId);

      if (membersError) throw membersError;

      if (!members || members.length === 0) {
        console.log('ℹ️ [AdherenceService] Nenhum membro encontrado na família');
        return [];
      }

      const memberIds = members.map(m => m.id);

      // Buscar adesão de todos os membros
      const { data, error } = await supabase
        .from('treatment_adherence_v2')
        .select('*')
        .in('member_id', memberIds);

      if (error) throw error;

      console.log(`✅ [AdherenceService] Adesão de ${data?.length || 0} membros encontrada (v2)`);
      return (data || []) as unknown as TreatmentAdherence[];
    } catch (error: any) {
      console.error('❌ [AdherenceService] Erro ao buscar adesão da família:', error);
      throw new Error(`Erro ao buscar adesão da família: ${error.message}`);
    }
  }

  /**
   * Formata a porcentagem de adesão para exibição
   * @param adherence - Dados de adesão
   * @returns String formatada (ex: "87%")
   */
  formatAdherencePercentage(adherence: TreatmentAdherence | null): string {
    if (!adherence || adherence.total_doses_expected === 0) {
      return '0%';
    }
    return `${Math.round(adherence.adherence_percentage)}%`;
  }

  /**
   * Retorna a cor apropriada baseada na porcentagem de adesão
   * @param percentage - Porcentagem de adesão (0-100)
   * @returns Classe CSS do Tailwind
   */
  getAdherenceColor(percentage: number): string {
    if (percentage >= 90) return 'text-emerald-600'; // Excelente
    if (percentage >= 75) return 'text-green-600';   // Bom
    if (percentage >= 60) return 'text-yellow-600';  // Regular
    if (percentage >= 40) return 'text-orange-600';  // Atenção
    return 'text-red-600';                            // Crítico
  }

  /**
   * Retorna o status textual baseado na porcentagem de adesão
   * @param percentage - Porcentagem de adesão (0-100)
   * @returns Status em português
   */
  getAdherenceStatus(percentage: number): string {
    if (percentage >= 90) return 'Excelente';
    if (percentage >= 75) return 'Bom';
    if (percentage >= 60) return 'Regular';
    if (percentage >= 40) return 'Atenção';
    return 'Crítico';
  }

  /**
   * Retorna ícone apropriado baseado na porcentagem de adesão
   * @param percentage - Porcentagem de adesão (0-100)
   * @returns Classe do ícone RemixIcon
   */
  getAdherenceIcon(percentage: number): string {
    if (percentage >= 90) return 'ri-checkbox-circle-fill';
    if (percentage >= 75) return 'ri-checkbox-circle-line';
    if (percentage >= 60) return 'ri-information-line';
    if (percentage >= 40) return 'ri-error-warning-line';
    return 'ri-close-circle-fill';
  }
}

// Exportar instância singleton
export const adherenceService = AdherenceService.getInstance();
